/**
 * Multiple workspaces.
 *
 * A workspace is one Yjs document (objects, chat, comments, presence…) plus a
 * row in the workspace registry. The pre-existing singleton document keeps its
 * name (`DOC_NAME`) and becomes the default workspace (`DEFAULT_WORKSPACE_ID`),
 * so existing data, links and IndexedDB caches keep working unchanged.
 *
 * URL shape: `/w/<workspaceId>` + the existing hash route (`#tab=…&obj=…`).
 * `/` (no prefix) is the default workspace. `/preview?w=<id>` for share pages.
 * Sync room: `${SYNC_PATH}/${workspaceDocName(id)}`.
 * History API: existing paths accept `?workspace=<id>` (default: main).
 * Blobs are shared across workspaces (keys are workspace-agnostic).
 * Dev Chat lives in the default document. Development deployments also reserve
 * a Device Cow workspace for live evidence artifacts.
 * The Aquaduck agent runs per workspace (one awareness client per document).
 */
import { DOC_NAME } from "./model.js";

export const DEFAULT_WORKSPACE_ID = "main";
/** Lowercase slug, 1–32 chars; ids are allocated by the server (`ws_` + random) unless a caller supplies a valid one. */
export const WORKSPACE_ID_RE = /^[a-z0-9][a-z0-9_-]{0,31}$/;
export const WORKSPACE_NAME_MAX = 64;
const DOC_PREFIX = "aquaduck-ws-";

export const workspaceDocName = (id: string): string => id === DEFAULT_WORKSPACE_ID ? DOC_NAME : `${DOC_PREFIX}${id}`;
export function workspaceIdFromDocName(doc: string): string | null {
  if (doc === DOC_NAME) return DEFAULT_WORKSPACE_ID;
  if (!doc.startsWith(DOC_PREFIX)) return null;
  const id = doc.slice(DOC_PREFIX.length);
  return WORKSPACE_ID_RE.test(id) ? id : null;
}
/** `/w/<id>` → id; anything else → the default workspace. */
export function workspaceIdFromPath(pathname: string): string {
  const m = /^\/w\/([^/]+)\/?$/.exec(pathname);
  if (!m) return DEFAULT_WORKSPACE_ID;
  try { const id = decodeURIComponent(m[1]!); return WORKSPACE_ID_RE.test(id) ? id : DEFAULT_WORKSPACE_ID; } catch { return DEFAULT_WORKSPACE_ID; }
}
/** The workspaces overview page (client route; the server serves the SPA there). Never a workspace itself. */
export const WORKSPACES_PAGE_PATH = "/workspaces";
export const isWorkspacesPage = (pathname: string): boolean => pathname === WORKSPACES_PAGE_PATH || pathname === `${WORKSPACES_PAGE_PATH}/`;
export const workspaceHref = (id: string, hash = ""): string => `/w/${encodeURIComponent(id)}` + hash;

export interface Workspace {
  id: string;
  name: string;
  description?: string;
  createdAt: number;
  createdBy: string; // userId
  /** Last content or metadata change (content writes are coalesced for up to 30 seconds). */
  updatedAt?: number;
  thumbnailUrl?: string | null;
  thumbnailAt?: number;
  updatedBy?: string;
  /** Soft-archived workspaces are hidden from the switcher by default and their agent is stopped; data is kept. */
  archivedAt?: number | null;
  archivedBy?: string | null;
}

/** GET → WorkspaceListResponse (`?includeArchived=1` to include archived). POST WorkspaceCreateRequest → Workspace (201). */
export const WORKSPACES_PATH = "/api/workspaces";
/** GET → Workspace; PATCH WorkspaceUpdateRequest → Workspace. 404 for unknown ids. The default workspace cannot be archived. */
export const workspacePath = (id: string) => `${WORKSPACES_PATH}/${encodeURIComponent(id)}`;
/** PUT WorkspaceThumbnailRequest → Workspace; 404 unknown, 410 archived. */
export const workspaceThumbnailPath = (id: string) => `${workspacePath(id)}/thumbnail`;
export interface WorkspaceThumbnailRequest { key: string; updatedBy: string }

export interface WorkspaceListResponse { workspaces: Workspace[] }
export interface WorkspaceCreateRequest { name: string; description?: string; createdBy: string; id?: string }
export interface WorkspaceUpdateRequest { name?: string; description?: string | null; archived?: boolean; updatedBy: string }

/** The registry always contains the default workspace; servers create this row on boot if missing. */
export const DEFAULT_WORKSPACE: Workspace = { id: DEFAULT_WORKSPACE_ID, name: "Workspace", createdAt: 0, createdBy: "system" };
